import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DesertScene from "../components/DesertScene";
import { usePlayer } from "../context/PlayerContext";
import { getStats, getMatches } from "../lib/api";
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { ArrowLeft, Crosshair, Flame, Skull, Timer, Zap, Star } from "lucide-react";
import { format } from "date-fns";

const StatCard = ({ icon: Icon, label, value, suffix = "", testid, accent = "var(--deepest-brown)" }) => (
  <div className="poster p-5" data-testid={testid}>
    <div className="flex items-center gap-2 font-elite text-xs tracking-widest text-[color:var(--dark-brown)]">
      <Icon size={14} color={accent} /> {label}
    </div>
    <div className="font-rye text-3xl sm:text-4xl mt-2 text-[color:var(--deepest-brown)]">
      {value}
      <span className="text-base ml-1 text-[color:var(--dark-brown)]">{suffix}</span>
    </div>
  </div>
);

const DiffPill = ({ id, label, played, won }) => {
  const wr = played ? Math.round((won / played) * 100) : 0;
  return (
    <div
      data-testid={`difficulty-stats-${id}`}
      className="border-2 border-[color:var(--deepest-brown)] bg-[color:var(--bone)] p-3 flex items-center justify-between"
    >
      <div>
        <div className="font-rye text-lg">{label}</div>
        <div className="font-elite text-xs text-[color:var(--dark-brown)]">
          {won}W / {played - won}L
        </div>
      </div>
      <div className="font-rye text-2xl text-[color:var(--dark-red)]">{wr}%</div>
    </div>
  );
};

const Dashboard = () => {
  const { username, clear } = usePlayer();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!username) {
      navigate("/");
      return;
    }
    Promise.all([getStats(username), getMatches(username, 10)])
      .then(([s, m]) => {
        setStats(s);
        setMatches(m);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [username, navigate]);

  if (!username) return null;

  return (
    <DesertScene>
      <div className="min-h-screen px-4 sm:px-10 py-8">
        <div className="flex items-center justify-between mb-8">
          <button
            data-testid="dashboard-back-button"
            onClick={() => navigate("/lobby")}
            className="font-elite text-sm flex items-center gap-2 text-[color:var(--deepest-brown)] hover:text-[color:var(--dark-red)]"
          >
            <ArrowLeft size={16} /> back to saloon
          </button>
          <div className="font-elite text-sm">
            slurpin' as <strong className="font-rye">{username}</strong>
          </div>
        </div>

        <div className="text-center mb-10">
          <div className="font-elite text-xs tracking-[0.3em] text-[color:var(--deepest-brown)]">
            — SHERIFF'S LEDGER —
          </div>
          <h1 className="font-rye title-3d text-[color:var(--yellow)] text-5xl sm:text-6xl mt-2">
            YOUR REPUTATION
          </h1>
        </div>

        {loading ? (
          <div className="text-center font-elite py-20">tallying your deeds...</div>
        ) : !stats || stats.total_matches === 0 ? (
          <div className="poster max-w-xl mx-auto p-10 text-center" data-testid="empty-stats">
            <div className="font-rye text-3xl text-[color:var(--deepest-brown)]">
              No deeds on record
            </div>
            <div className="font-elite text-sm mt-3 text-[color:var(--dark-brown)]">
              Hit the lobby and pick a fight, partner.
            </div>
            <button
              onClick={() => navigate("/lobby")}
              className="btn-western red mt-6"
              data-testid="go-to-lobby-button"
            >
              Find a Duel
            </button>
          </div>
        ) : (
          <>
            {/* stats grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 max-w-6xl mx-auto">
              <StatCard
                icon={Crosshair}
                label="DUELS"
                value={stats.total_matches}
                testid="stat-total-duels"
              />
              <StatCard
                icon={Flame}
                label="WIN RATE"
                value={stats.win_rate}
                suffix="%"
                testid="stat-win-rate"
                accent="var(--dark-red)"
              />
              <StatCard
                icon={Timer}
                label="BEST DRAW"
                value={stats.best_reaction_ms ?? "—"}
                suffix={stats.best_reaction_ms ? "ms" : ""}
                testid="stat-best-reaction"
              />
              <StatCard
                icon={Zap}
                label="STREAK"
                value={stats.current_streak}
                suffix={stats.current_streak === 1 ? " win" : " wins"}
                testid="stat-current-streak"
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 max-w-6xl mx-auto mt-4">
              <StatCard
                icon={Star}
                label="LONGEST STREAK"
                value={stats.longest_streak}
                testid="stat-longest-streak"
              />
              <StatCard
                icon={Timer}
                label="AVG REACTION"
                value={stats.avg_reaction_ms ?? "—"}
                suffix={stats.avg_reaction_ms ? "ms" : ""}
                testid="stat-avg-reaction"
              />
              <StatCard
                icon={Skull}
                label="FOULS"
                value={stats.fouls}
                testid="stat-fouls"
              />
            </div>

            {/* chart + difficulty */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-6xl mx-auto mt-8">
              <div className="lg:col-span-2 poster p-6" data-testid="reaction-chart-card">
                <div className="font-rye text-2xl mb-1 text-[color:var(--deepest-brown)]">
                  Reaction Times
                </div>
                <div className="font-elite text-xs text-[color:var(--dark-brown)] mb-4">
                  last {stats.reaction_history?.length || 0} valid draws (ms)
                </div>
                <div style={{ width: "100%", height: 260 }}>
                  <ResponsiveContainer>
                    <LineChart
                      data={(stats.reaction_history || []).map((p, i) => ({
                        idx: i + 1,
                        ms: p.ms,
                      }))}
                    >
                      <CartesianGrid stroke="#5d3b00" strokeOpacity={0.2} strokeDasharray="3 3" />
                      <XAxis
                        dataKey="idx"
                        stroke="#5d3b00"
                        tick={{ fontFamily: "Special Elite", fontSize: 11 }}
                      />
                      <YAxis
                        stroke="#5d3b00"
                        tick={{ fontFamily: "Special Elite", fontSize: 11 }}
                      />
                      <Tooltip
                        contentStyle={{
                          background: "#fbf3df",
                          border: "2px solid #5d3b00",
                          fontFamily: "Special Elite",
                        }}
                        formatter={(v) => [`${v} ms`, "reaction"]}
                      />
                      <Line
                        type="monotone"
                        dataKey="ms"
                        stroke="#e60808"
                        strokeWidth={3}
                        dot={{ r: 4, fill: "#ffda00", stroke: "#5d3b00", strokeWidth: 1.5 }}
                        activeDot={{ r: 6 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="poster p-6" data-testid="difficulty-breakdown-card">
                <div className="font-rye text-2xl mb-4 text-[color:var(--deepest-brown)]">
                  By Foe
                </div>
                <div className="grid grid-cols-1 gap-3">
                  <DiffPill id="rookie" label="Rookie Slinger" {...(stats.by_difficulty.rookie || {played:0,won:0})} />
                  <DiffPill id="bandit" label="Saucy Bandit" {...(stats.by_difficulty.bandit || {played:0,won:0})} />
                  <DiffPill id="sheriff" label="Sheriff of Slurps" {...(stats.by_difficulty.sheriff || {played:0,won:0})} />
                </div>
                <div className="mt-5">
                  <div className="font-elite text-xs text-[color:var(--dark-brown)]">
                    Accuracy (non-foul rounds)
                  </div>
                  <div className="font-rye text-3xl text-[color:var(--deepest-brown)]">
                    {stats.accuracy}%
                  </div>
                </div>
              </div>
            </div>

            {/* recent matches */}
            <div className="max-w-6xl mx-auto mt-8 poster p-6" data-testid="recent-matches">
              <div className="font-rye text-2xl mb-4 text-[color:var(--deepest-brown)]">
                Recent Duels
              </div>
              <div className="overflow-x-auto">
                <table className="w-full font-elite text-sm">
                  <thead>
                    <tr className="text-left border-b-2 border-dashed border-[color:var(--dark-brown)]">
                      <th className="py-2">DATE</th>
                      <th>FOE</th>
                      <th>SCORE</th>
                      <th>BEST</th>
                      <th>AVG</th>
                      <th className="text-right">VERDICT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {matches.map((m, i) => (
                      <tr
                        key={m.id}
                        className="border-b border-dashed border-[color:var(--dark-brown)]/40"
                        style={{ background: i % 2 === 0 ? "transparent" : "rgba(124,63,10,0.07)" }}
                      >
                        <td className="py-2">
                          {format(new Date(m.created_at), "MMM d, HH:mm")}
                        </td>
                        <td className="capitalize">{m.difficulty}</td>
                        <td>
                          {m.rounds_won}–{m.rounds_lost}
                        </td>
                        <td>{m.best_reaction_ms ? `${m.best_reaction_ms}ms` : "—"}</td>
                        <td>{m.avg_reaction_ms ? `${Math.round(m.avg_reaction_ms)}ms` : "—"}</td>
                        <td className="text-right">
                          <span
                            className={`px-2 py-0.5 text-[color:var(--bone)] font-rye ${
                              m.won ? "bg-[color:var(--chicken-green)]" : "bg-[color:var(--red)]"
                            }`}
                          >
                            {m.won ? "WON" : "LOST"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="text-center mt-10 flex flex-wrap gap-3 justify-center">
              <button
                onClick={() => navigate("/lobby")}
                className="btn-western red"
                data-testid="dashboard-play-button"
              >
                Saddle Up Again
              </button>
              <button
                onClick={() => {
                  clear();
                  navigate("/");
                }}
                className="btn-western"
                style={{ background: "var(--bone)" }}
                data-testid="dashboard-signout-button"
              >
                Sign Out
              </button>
            </div>
          </>
        )}
      </div>
    </DesertScene>
  );
};

export default Dashboard;
